import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import { Customer, InvoiceReminder, InvoiceStatus } from '../invoices.model';
import { ReminderConfig, ScheduleRelation } from 'components/reminder-modal/types';
import { ReminderApplyOn } from 'containers/settings/constants';
import { PredefinedOption } from 'components/day-picker/DayPicker';
import { InfoType } from './constants';
import { GetMerchantCustomizationsResponse } from 'containers/settings/settings.model';
import { InvoicesState } from '../InvoicesSlice';
import { DiscountFormData } from '../types';
import { DiscountV2, InvoicePayloadV2, ServiceFeeV2, TipV2 } from '../invoices.model';
import { isValidDiscountData } from '../utils';
import {
  getAttachmentArray,
  getLineItemsArrayForEvent,
  getServiceChargeEventPayload,
  prepareDiscountRelatedPayload,
  prepareLineItemList,
  prepareServiceChargeListRequestPayload,
  prepareSummatedUniqueTaxList
} from '../utils';
import { InvoiceSourceEnum, InvoiceVersion } from '../constants';
import { IUniqueTaxRequestV2, InvoicePayload, LineItemV2, ServiceChargeV2 } from '../invoices.model';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';
import { constructArrayObjectAsString } from 'utils/commonUtils';
import { RelatedEntityData } from './types';

export const calcLineItemTotal = (unitCost: number | string = 0, quantity: number | string = 0) => {
  if (unitCost === '.') {
    return null;
  }
  return getAmountWithCurrency(+unitCost * +quantity, '$');
};

export const isValidNumber = (value: string): boolean => {
  return value !== '' && !isNaN(+value);
};

export const getInvoiceRemainderDaysText = ({
  status,
  createdAt,
  lastTrnxDate
}: {
  status: InvoiceStatus;
  createdAt: string;
  lastTrnxDate: string;
}) => {
  let statusDate: Date;
  if (status === InvoiceStatus.PENDING) {
    statusDate = new Date(createdAt);
    const days = DateUtils.getDifferenceInDays({ secondaryDate: statusDate });
    if (days === 0) {
      return 'Since today';
    }
    return `Since ${days} ${days > 1 ? 'Days' : 'Day'}`;
  }
  statusDate = new Date(lastTrnxDate);
  const days = DateUtils.getDifferenceInDays({ secondaryDate: statusDate });
  if (days === 0) {
    return 'Today';
  }
  return `${days} ${days > 1 ? 'Days' : 'Day'} ago`;
};

// Calculate default due date based on duration from settings
export const getDueDate = (referenceDate: Date, durationInDays: number): Date => {
  const calculatedDate = DateUtils.addDaysToDate(referenceDate, durationInDays);
  // Normalize to end of day
  return DateUtils.getEndOfDay(calculatedDate);
};

// Validate if reminders can be enabled
export const validateInvoiceReminderPrerequisites = (
  customer: Customer
): { canEnable: boolean; errorMessage: string; customerSectionMsg?: string; msgType: InfoType | null } => {
  // eslint-disable-next-line quotes
  const customerInfoMsg = "Want to send reminders? Add the customer's email and set up reminders below";
  if (!customer) {
    return {
      canEnable: false,
      errorMessage: 'Payment reminders cannot be sent due to missing customer.',
      msgType: InfoType.ERROR
    };
  } else if (!customer.email) {
    return {
      canEnable: false,
      errorMessage: 'Payment reminders cannot be sent due to missing customer email address.',
      // eslint-disable-next-line quotes
      customerSectionMsg: customerInfoMsg,
      msgType: InfoType.WARN
    };
  }
  return { canEnable: true, errorMessage: '', customerSectionMsg: '', msgType: null };
};

// Transform component reminders to API format for invoice create/edit
export const transformInvoiceRemindersToApiFormat = (reminders: ReminderConfig[]): InvoiceReminder[] => {
  return reminders.map((reminder, index) => ({
    ...(reminder?.reminderId ? { id: reminder?.reminderId } : {}),
    name: `Reminder ${index + 1}`,
    condition: {
      applyOn: ReminderApplyOn.DUE_DATE,
      timing: reminder.scheduleRelation,
      offset: reminder.days
    },
    remindOn: reminder.reminderDate || null,
    isSent: reminder.isSent || false
  }));
};

// Transform API reminders to component format
export const transformInvoiceApiRemindersToComponentFormat = (reminders: InvoiceReminder[]): ReminderConfig[] => {
  return reminders?.length > 0
    ? reminders.map((reminder, index) => ({
        id: `${index + 1}-${reminder.id}`,
        reminderNumber: index + 1,
        scheduleRelation: reminder.condition.timing as ScheduleRelation,
        days: reminder.condition.offset,
        reminderDate: reminder.remindOn || null,
        reminderId: reminder.id || null,
        isSent: reminder.isSent || false
      }))
    : [];
};

/**
 * Matcher function for date options (due dates, expiry dates, etc.)
 */
export const dateOptionMatcher = (option: PredefinedOption, selectedDate: Date): boolean => {
  // Custom option never matches based on date
  if (option.isCustom) {
    return false;
  }

  // Use the already calculated date from the option
  if (option.date) {
    return DateUtils.areSameDates(option.date, selectedDate);
  }

  return false;
};

// V1 Create Invoice Payload Function
// TODO: Remove this function once V2 released
export const getCreateInvoicePayload = (
  selectedInvoice: InvoicesState,
  currentTeam: { id: number; storeName: string },
  tncData: { enabled: boolean; tncId: number },
  merchantCustomisations: GetMerchantCustomizationsResponse,
  selectedIsSignatureEnabled: boolean,
  invoiceReferenceNumber?: string,
  estimateId?: string,
  invoiceFromEstimateNumber?: string
): InvoicePayload => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayload(selectedInvoice.selectedServiceCharge);

  const invoicePayload: InvoicePayload = {
    version: InvoiceVersion.THREE_POINT_ZERO,
    teamId: currentTeam.id.toString(),
    storeName: currentTeam.storeName,
    // store all level amounts in redux slice eg: subtotal, finalamount etc
    amount: selectedInvoice.invoiceSubTotal.toString(),
    comment: selectedInvoice.comment,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    lineItems: prepareLineItemList(selectedInvoice.invoiceLineItems),
    ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
    surchargePercentage: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
      : '0',
    surchargeAmount: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.valueInCurrencyWithBps?.toString() ?? '0'
      : '',
    isServiceFeeEnabled: selectedInvoice.isServiceFeeEnabled,
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachments),
    tipEnabled: selectedInvoice.tipEnabled,
    tncId: tncData.enabled ? tncData.tncId : 0,
    ...prepareSummatedUniqueTaxList({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItems,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : '0',
    source: InvoiceSourceEnum.WEB,
    isSignatureEnabled: selectedIsSignatureEnabled,
    serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? null,
    paymentModes: getPaymentModes(selectedInvoice.invoicePaymentModes, merchantCustomisations.paymentModes),
    ...(estimateId
      ? { relativeEntity: { entityType: 'ESTIMATE', entityId: estimateId, entityNumber: invoiceFromEstimateNumber } }
      : {})
  };

  const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
  logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceSave, {
    customerName: selectedInvoice?.invoiceCustomer?.name,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    teamId: currentTeam.id.toString(),
    storeName: currentTeam.storeName,
    amount: selectedInvoice.invoiceSubTotal.toString(),
    'Line Items': constructArrayObjectAsString(
      getLineItemsArrayForEvent(selectedInvoice.invoiceLineItems),
      'Line item'
    ),
    ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
    surchargePercentage: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
      : '0',
    ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
    serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? ''
  });
  return invoicePayload;
};

// V2-Specific Prepare Functions for Nested Objects

export const prepareV2ServiceFeePayload = (invoiceState: InvoicesState): ServiceFeeV2 => {
  if (invoiceState.isServiceFeeEnabled) {
    return {
      enabled: invoiceState.isServiceFeeEnabled,
      type: 'PERCENTAGE',
      value: invoiceState.isServiceFeeEnabled ? +(invoiceState.currentSurcharge.percentage ?? '0') : 0,
      basePoints: invoiceState.isServiceFeeEnabled ? +(invoiceState.currentSurcharge.basePoints ?? '0') : 0,
      amount: invoiceState.isServiceFeeEnabled ? +(invoiceState.currentSurcharge.valueInCurrencyWithBps ?? 0) : 0
    };
  }
  return {
    enabled: false,
    type: 'PERCENTAGE',
    value: 0,
    basePoints: 0,
    amount: 0
  };
};

export const prepareV2TipPayload = (invoiceState: InvoicesState): TipV2 => {
  return {
    enabled: invoiceState.tipEnabled,
    ...(invoiceState.tipEnabled && invoiceState.partialPaymentEnabled ? { type: invoiceState.tipTiming } : {})
  };
};

export const prepareV2DiscountPayload = (discountFormData: DiscountFormData): DiscountV2 => {
  if (isValidDiscountData(discountFormData)) {
    return {
      enabled: true,
      type: discountFormData.isDiscountAmountSelected ? 'ABSOLUTE' : 'PERCENTAGE',
      value: discountFormData.isDiscountAmountSelected
        ? +discountFormData.discountAmount
        : +discountFormData.discountPercent,
      amount: +discountFormData.discountInCurrency
    };
  }

  return {
    enabled: false,
    type: 'PERCENTAGE',
    value: 0,
    amount: 0
  };
};

// V2 Helper Functions using existing functions with .map() for type conversions

export const prepareLineItemListV2 = (invoiceLineItems: InvoicesState['invoiceLineItems']) => {
  const v1LineItems = prepareLineItemList(invoiceLineItems);
  return v1LineItems.map(item => ({
    ...item,
    qty: +item.qty,
    amount: +item.amount,
    unitPrice: +item.unitPrice,
    unitCost: item.unitCost ? +item.unitCost : undefined,
    markupPercentage: item.markupPercentage ? +item.markupPercentage : undefined,
    taxes: item.taxes?.map(tax => ({
      ...tax,
      percentage: +tax.percentage,
      amount: +tax.amount
    }))
  })) as LineItemV2[];
};

export const prepareSummatedUniqueTaxListV2 = (params: {
  appliedLineItemLevelTaxes: InvoicesState['appliedLineItemLevelTaxes'];
  invoiceLineItems: InvoicesState['invoiceLineItems'];
  selectedServiceCharge: InvoicesState['selectedServiceCharge'];
}) => {
  const v1TaxesResult = prepareSummatedUniqueTaxList(params);
  const v1Taxes = v1TaxesResult.taxes || [];
  return v1Taxes.map((tax: any) => ({
    catalogueTaxId: tax.catalogueTaxId,
    name: tax.name,
    percentage: +tax.percentage,
    amount: +tax.amount
  })) as IUniqueTaxRequestV2[];
};

export const prepareServiceChargeListRequestPayloadV2 = (serviceCharge: InvoicesState['selectedServiceCharge']) => {
  const v1ServiceCharges = prepareServiceChargeListRequestPayload(serviceCharge);
  return v1ServiceCharges.map(charge => ({
    ...charge,
    amount: +charge.amount,
    percentage: +charge.percentage,
    totalAmount: +charge.totalAmount,
    taxes: charge.taxes?.map(tax => ({
      ...tax,
      percentage: +tax.percentage,
      amount: +tax.amount
    }))
  })) as ServiceChargeV2[];
};

// V2 Create Invoice Payload Function
export const getCreateInvoiceV2Payload = (
  selectedInvoice: InvoicesState,
  currentTeam: { id: number; storeName: string },
  tncData: { enabled: boolean; tncId: number },
  merchantCustomisations: GetMerchantCustomizationsResponse,
  entityData?: RelatedEntityData
): InvoicePayloadV2 => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayloadV2(selectedInvoice.selectedServiceCharge);

  const invoicePayload: InvoicePayloadV2 = {
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    customerServiceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? undefined,
    invoiceDate: selectedInvoice.invoiceDate || new Date().toISOString(),
    invoiceDueDate: selectedInvoice.dueDate ?? undefined,
    lineItems: prepareLineItemListV2(selectedInvoice.invoiceLineItems),
    lineItemAmount: selectedInvoice.lineItemsTotal,
    discount: prepareV2DiscountPayload(selectedInvoice.discountFormData),
    subTotalAmount: selectedInvoice.invoiceSubTotal,
    serviceFee: prepareV2ServiceFeePayload(selectedInvoice),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : 0,
    taxes: prepareSummatedUniqueTaxListV2({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItems,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    totalAmount: selectedInvoice.finalAmount,
    note: selectedInvoice.comment || '',
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachments),
    paymentMethodList: getPaymentModes(selectedInvoice.invoicePaymentModes, merchantCustomisations.paymentModes),
    tip: prepareV2TipPayload(selectedInvoice),
    partialPaymentEnabled: selectedInvoice.partialPaymentEnabled,
    reminderEnabled: selectedInvoice.paymentRemindersEnabled,
    reminders: transformInvoiceRemindersToApiFormat(selectedInvoice.paymentReminders) ?? [],
    signatureEnabled: selectedInvoice.isSignatureEnabled,
    tncId: tncData.enabled ? tncData.tncId : 0,
    referenceId: selectedInvoice.invoiceReferenceNumber,
    storeName: currentTeam.storeName,
    version: InvoiceVersion.THREE_POINT_ONE,
    source: InvoiceSourceEnum.WEB
  };

  // Add relative entity if creating from estimate or job
  if (entityData?.entityId && entityData?.entityType) {
    invoicePayload.relativeEntity = {
      entityType: entityData.entityType,
      entityId: entityData.entityId,
      entityNumber: entityData.entityNumber
    };
  }

  const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
  logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceSave, {
    isPartialPaymentEnabled: selectedInvoice.partialPaymentEnabled,
    isReminderEnabled: selectedInvoice.paymentRemindersEnabled,
    reminderCount: selectedInvoice.paymentReminders.length,
    dueDate: selectedInvoice.dueDate ? DateUtils.parseISODateString(selectedInvoice.dueDate) : null,
    customerName: selectedInvoice?.invoiceCustomer?.name,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    teamId: currentTeam.id.toString(),
    storeName: currentTeam.storeName,
    amount: selectedInvoice.invoiceSubTotal.toString(),
    'Line Items': constructArrayObjectAsString(
      getLineItemsArrayForEvent(selectedInvoice.invoiceLineItems),
      'Line item'
    ),
    ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
    surchargePercentage: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
      : '0',
    ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
    serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? ''
  });

  const defaultReminderSettings = selectedInvoice.invoiceDefaultReminderFromSetting;
  if (defaultReminderSettings) {
    const remindersWereEdited = areRemindersEdited(
      selectedInvoice.paymentReminders,
      selectedInvoice.paymentRemindersEnabled,
      defaultReminderSettings.reminders,
      defaultReminderSettings.enabled
    );

    if (remindersWereEdited) {
      logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceRemindersEdited, {
        customerName: selectedInvoice?.invoiceCustomer?.name,
        customerId: selectedInvoice?.invoiceCustomer?.ID,
        teamId: currentTeam.id.toString(),
        storeName: currentTeam.storeName,
        remindersEnabled: selectedInvoice.paymentRemindersEnabled,
        remindersCount: selectedInvoice.paymentReminders.length,
        defaultRemindersEnabled: defaultReminderSettings.enabled,
        defaultRemindersCount: defaultReminderSettings.reminders.length
      });
    }
  }

  return invoicePayload;
};

// PDF Preview Payload for Create Invoice
export const getPDFPreviewPayload = (
  selectedInvoice: InvoicesState,
  currentTeam: { id: number; storeName: string },
  tncData: { enabled: boolean; tncId: number },
  invoiceNumber: string
) => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayloadV2(selectedInvoice.selectedServiceCharge);

  return {
    invoiceNumber,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    customerServiceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? undefined,
    invoiceDate: selectedInvoice.invoiceDate || new Date().toISOString(),
    invoiceDueDate: selectedInvoice.dueDate ?? undefined,
    lineItems: prepareLineItemListV2(selectedInvoice.invoiceLineItems),
    lineItemAmount: selectedInvoice.lineItemsTotal,
    discount: prepareV2DiscountPayload(selectedInvoice.discountFormData),
    subTotalAmount: selectedInvoice.invoiceSubTotal,
    serviceFee: prepareV2ServiceFeePayload(selectedInvoice),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : 0,
    taxes: prepareSummatedUniqueTaxListV2({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItems,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    totalAmount: selectedInvoice.finalAmount,
    note: selectedInvoice.comment || '',
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachments),
    tip: prepareV2TipPayload(selectedInvoice),
    partialPaymentEnabled: selectedInvoice.partialPaymentEnabled,
    reminderEnabled: selectedInvoice.paymentRemindersEnabled,
    signatureEnabled: selectedInvoice.isSignatureEnabled,
    tncId: tncData.enabled ? tncData.tncId : 0,
    referenceId: selectedInvoice.invoiceReferenceNumber || '',
    storeName: currentTeam.storeName,
    version: InvoiceVersion.THREE_POINT_ONE,
    source: InvoiceSourceEnum.WEB
  };
};

// Payment modes utility function
export const getPaymentModes = (
  invoicePaymentModes: InvoicesState['invoicePaymentModes'],
  merchantPaymentModes: GetMerchantCustomizationsResponse['paymentModes']
) => {
  const selectedModes = invoicePaymentModes.filter(item => item.isSelected).map(item => item.id);

  const updatedPaymentModes = merchantPaymentModes.map(paymentMode => {
    if (paymentMode.teamLevelVisiblity) {
      if (selectedModes.includes(paymentMode.id)) {
        return {
          id: paymentMode.id,
          enabled: true
        };
      }
      return {
        id: paymentMode.id,
        enabled: false
      };
    }
    return {
      id: paymentMode.id,
      enabled: paymentMode.enabled
    };
  });
  return updatedPaymentModes;
};

const areRemindersEdited = (
  currentReminders: ReminderConfig[],
  currentEnabled: boolean,
  defaultReminders: ReminderConfig[],
  defaultEnabled: boolean
): boolean => {
  // Check if enabled status changed
  if (currentEnabled !== defaultEnabled) {
    return true;
  }

  // If reminders are disabled, no need to check further
  if (!currentEnabled) {
    return false;
  }

  if (currentReminders.length !== defaultReminders.length) {
    return true;
  }

  for (let i = 0; i < currentReminders.length; i++) {
    const current = currentReminders[i];
    const defaultReminder = defaultReminders[i];

    if (current.scheduleRelation !== defaultReminder?.scheduleRelation || current.days !== defaultReminder?.days) {
      return true;
    }
  }

  return false;
};
